/**
 * Daily reminder by web push. The browser subscribes with the server's VAPID key; the Worker's
 * cron then sends one payload-less push a day at the chosen local time, unless today's round is
 * already done. iPhone supports this only once the site is added to the Home Screen.
 */
import { useSyncExternalStore } from 'react';
import { track } from './analytics';
import { api } from './api';
import { readJson, removeKey, STORAGE_KEYS, writeJson } from './storage';

export const DEFAULT_REMINDER_MINUTE = 9 * 60;

interface Stored {
  minuteOfDay: number;
  /** Local date of the last refresh, so the app re-sends the subscription at most once a day. */
  refreshed: string;
}

const isStored = (v: unknown): v is Stored =>
  typeof v === 'object' &&
  v !== null &&
  typeof (v as Stored).minuteOfDay === 'number' &&
  typeof (v as Stored).refreshed === 'string';

const listeners = new Set<() => void>();
const emit = (): void => listeners.forEach((l) => l());

export function reminderSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/** Reminder time on this device, or null when off. */
export const reminderMinute = (): number | null =>
  readJson(STORAGE_KEYS.reminder, isStored)?.minuteOfDay ?? null;

export function useReminder(): number | null {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    reminderMinute,
    () => null,
  );
}

export const formatMinute = (m: number): string =>
  `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`;

const localDate = (): string => new Date().toLocaleDateString('en-CA');

function keyBytes(b64url: string): Uint8Array<ArrayBuffer> {
  const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4));
  return Uint8Array.from(raw, (ch) => ch.charCodeAt(0));
}

async function registration(): Promise<ServiceWorkerRegistration> {
  return (
    (await navigator.serviceWorker.getRegistration()) ?? navigator.serviceWorker.register('/sw.js')
  );
}

async function save(endpoint: string, minuteOfDay: number): Promise<void> {
  await api('/push/subscribe', {
    method: 'POST',
    body: { endpoint, minuteOfDay, tzOffset: new Date().getTimezoneOffset() },
  });
  writeJson(STORAGE_KEYS.reminder, { minuteOfDay, refreshed: localDate() } satisfies Stored);
  emit();
}

export type ReminderResult = 'on' | 'denied' | 'unsupported' | 'failed';

/** Ask for permission (needs a tap), subscribe and save the time. Also used to change the time. */
export async function enableReminder(
  minuteOfDay = DEFAULT_REMINDER_MINUTE,
): Promise<ReminderResult> {
  if (!reminderSupported()) return 'unsupported';
  try {
    if ((await Notification.requestPermission()) !== 'granted') return 'denied';
    const { publicKey } = await api<{ publicKey: string | null }>('/push');
    if (!publicKey) return 'unsupported';
    const reg = await registration();
    const sub =
      (await reg.pushManager.getSubscription()) ??
      (await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: keyBytes(publicKey),
      }));
    await save(sub.endpoint, minuteOfDay);
    track('reminder_enabled', { minute: minuteOfDay });
    return 'on';
  } catch {
    return 'failed';
  }
}

export async function disableReminder(): Promise<void> {
  removeKey(STORAGE_KEYS.reminder);
  emit();
  track('reminder_disabled');
  if (!reminderSupported()) return;
  try {
    const sub = await (await registration()).pushManager.getSubscription();
    if (!sub) return;
    await api('/push/unsubscribe', { method: 'POST', body: { endpoint: sub.endpoint } }).catch(
      () => undefined,
    );
    await sub.unsubscribe();
  } catch {
    /* already gone */
  }
}

/**
 * On app start, at most once a day: re-send the subscription so the server has the current
 * clock offset (summer time) and a fresh endpoint. Drops the setting if permission was revoked.
 */
export async function refreshReminder(): Promise<void> {
  const stored = readJson(STORAGE_KEYS.reminder, isStored);
  if (!stored || stored.refreshed === localDate() || !reminderSupported()) return;
  try {
    if (Notification.permission !== 'granted') {
      removeKey(STORAGE_KEYS.reminder);
      emit();
      return;
    }
    const sub = await (await registration()).pushManager.getSubscription();
    if (!sub) {
      removeKey(STORAGE_KEYS.reminder);
      emit();
      return;
    }
    await save(sub.endpoint, stored.minuteOfDay);
  } catch {
    /* try again next time */
  }
}
